import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What must an Australian invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'An Australian tax invoice must include: the words "Tax Invoice"; your business name and ABN; the date of issue; the buyer name or ABN (for invoices over $1,000); a description of the goods or services; the GST amount; and the total price including GST. For invoices under $1,000, a simplified tax invoice format is acceptable.',
      },
    },
    {
      '@type': 'Question',
      name: 'When do I need to charge GST in Australia?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You must register for GST and charge 10% GST if your annual business turnover is $75,000 or more (or $150,000 for non-profit organisations). Once registered, you issue tax invoices showing the GST component. If your turnover is below $75,000, you can choose to register voluntarily.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I create a free Australian invoice online?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is a free invoice generator for Australian businesses and freelancers. Create tax invoices with GST auto-calculated, or standard invoices for businesses below the GST threshold. Download a professional PDF instantly, no sign-up needed for up to 5 invoices per month.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I show GST on an Australian invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Show the GST as a separate line: the price excluding GST, then the GST amount (10%), then the total price including GST. Invilic calculates and formats this automatically when you select the VAT invoice type and enter 10% as the rate.',
      },
    },
    {
      '@type': 'Question',
      name: 'What currency should I use on Australian invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'For Australian clients, use AUD (Australian Dollar, $). For international clients, you can bill in USD, GBP, EUR, or any other currency using the international invoice type in Invilic.',
      },
    },
  ],
};

export default function InvoiceGeneratorAustraliaPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Australia: Free GST Tax Invoice Maker | Invilic"
        description="Free invoice generator for Australian businesses and freelancers. Create GST tax invoices with ABN, auto 10% GST calculation. Standard invoices for businesses under threshold. PDF free."
        canonical="https://www.invilic.com/invoice-generator-australia"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇦🇺 Australia
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Australia
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for Australian businesses, freelancers, and sole traders. Create GST tax invoices with 10% GST auto-calculated, or standard invoices for businesses below the $75,000 threshold. Download professional PDF invoices instantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/create/vat"
              className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-emerald-600/20 transition-all duration-200"
            >
              Create GST Tax Invoice <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/create/freelancer"
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200"
            >
              Create Standard Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        {/* Invoice types */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Which Invoice Do You Need?
          </h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              {
                icon: Percent,
                title: 'GST Tax Invoice',
                subtitle: 'For GST-registered businesses',
                features: ['10% GST auto-calculated', 'ABN field included', 'Net + GST + total breakdown', 'ATO-compliant format'],
                to: '/create/vat',
                color: 'emerald',
                btnClass: 'bg-emerald-600 hover:bg-emerald-700',
              },
              {
                icon: FileText,
                title: 'Standard Invoice',
                subtitle: 'For businesses under $75,000 turnover',
                features: ['No GST required', 'Custom line items', 'Logo upload', 'AUD currency', 'Payment terms'],
                to: '/create/freelancer',
                color: 'blue',
                btnClass: 'bg-blue-600 hover:bg-blue-700',
              },
              {
                icon: Globe,
                title: 'International Invoice',
                subtitle: 'For billing overseas clients',
                features: ['USD, GBP, EUR billing', 'SWIFT / IBAN fields', 'Export invoice format', 'No local GST required'],
                to: '/create/international',
                color: 'indigo',
                btnClass: 'bg-indigo-600 hover:bg-indigo-700',
              },
            ].map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={to}
                  className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}
                >
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* GST guide */}
        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">
            GST Tax Invoice vs Standard Invoice: Which Do You Need?
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-emerald-700 dark:text-emerald-400 mb-3">Use a GST Tax Invoice if:</div>
              {[
                'Your annual turnover is $75,000 or more (or $150,000 for non-profits)',
                'You are registered for GST with the ATO',
                'Your client needs a tax invoice to claim GST credits',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-blue-700 dark:text-blue-400 mb-3">Use a Standard Invoice if:</div>
              {[
                'Your turnover is below the $75,000 GST registration threshold',
                'You are a freelancer or sole trader not registered for GST',
                'Your client does not need a GST tax invoice',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Australian Invoice Generator: Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Australian Invoice for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF for Australian businesses in under 2 minutes.</p>
          <Link
            to="/create/vat"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-emerald-600/20 transition-all duration-200 text-lg"
          >
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
